import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const bookOpen = keyframes`
  0% {
    transform: perspective(1000px) rotateY(0deg) scale(0.8);
    opacity: 0;
  }
  50% {
    transform: perspective(1000px) rotateY(-15deg) scale(0.9);
    opacity: 0.7;
  }
  100% {
    transform: perspective(1000px) rotateY(0deg) scale(1);
    opacity: 1;
  }
`;

const pageTurn = keyframes`
  0% {
    transform: rotateY(0deg);
  }
  50% {
    transform: rotateY(-5deg);
  }
  100% {
    transform: rotateY(0deg);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(5px);
`;

const BookContainer = styled.div`
  position: relative;
  width: 90vw;
  max-width: 1000px;
  height: 80vh;
  max-height: 700px;
  perspective: 1000px;
  animation: ${bookOpen} 1.2s ease-out;
`;

const Book = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.6s ease;
`;

const BookCover = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #8B0000 0%, #A52A2A 50%, #8B0000 100%);
  border-radius: 8px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 0 0 3px #FFD700,
    inset 0 0 0 6px #8B0000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  padding: 2rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 20px;
    left: 20px;
    right: 20px;
    bottom: 20px;
    border: 2px solid #FFD700;
    border-radius: 4px;
    opacity: 0.6;
  }
`;

const BookTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  margin: 0 0 1rem 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  color: #FFD700;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const BookSubtitle = styled.h2`
  font-size: 1.5rem;
  margin: 0 0 2rem 0;
  font-style: italic;
  color: #FFD700;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BookPages = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #F5F5DC;
  border-radius: 8px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 0 0 1px #D2B48C;
  padding: 3rem;
  overflow-y: auto;
  transform: translateZ(1px);
`;

const PageContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
  color: #2C1810;
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: #8B0000;
  margin: 0 0 2rem 0;
  text-align: center;
  border-bottom: 3px solid #FFD700;
  padding-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PageText = styled.p`
  font-size: 1.1rem;
  margin: 0 0 1.5rem 0;
  text-align: justify;
  
  &:first-of-type {
    font-size: 1.3rem;
    font-weight: 600;
    color: #8B0000;
    text-align: center;
    margin-bottom: 2rem;
  }
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 215, 0, 0.05));
  border: 2px solid #FFD700;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 2rem 0;
  text-align: center;
`;

const HighlightText = styled.p`
  font-size: 1.2rem;
  font-weight: 600;
  color: #8B0000;
  margin: 0;
  font-style: italic;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10001;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const ChristmasAboutModal = ({ isOpen, onClose }) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setShowContent(true), 800);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <BookContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        <Book>
          <BookCover>
            <BookTitle>Step into the Story</BookTitle>
            <BookSubtitle>A Christmas Tale at Belvoir Castle</BookSubtitle>
          </BookCover>
          
          {showContent && (
            <BookPages>
              <PageContent>
                <PageTitle>Step into the Story</PageTitle>
                
                <PageText>
                  Welcome to a Christmas like no other, where every corner of Belvoir Castle tells a story of festive magic and timeless wonder.
                </PageText>
                
                <PageText>
                  This Christmas, our historic estate transforms into a living storybook, where centuries of tradition meet the joy of the season. As you wander through our grand State Rooms, you'll discover displays that bring to life the rich heritage of this magnificent castle, each room carefully dressed to tell its own chapter of the Christmas story.
                </PageText>
                
                <PageText>
                  The Castle's grand architecture provides the perfect backdrop for our festive celebrations. From the soaring ceilings adorned with twinkling lights to the ornate fireplaces crackling with warmth, every detail has been crafted to create an atmosphere of pure enchantment. Our team of designers and craftspeople have worked tirelessly to ensure that each space tells its own unique story while contributing to the overall narrative of Christmas at Belvoir.
                </PageText>
                
                <HighlightBox>
                  <HighlightText>
                    "This Christmas, we invite you to become part of our story, creating memories that will last a lifetime in the magical setting of Belvoir Castle."
                  </HighlightText>
                </HighlightBox>
                
                <PageText>
                  The Castle's rich history adds depth to every festive experience. Built in the 19th century and home to the Duke and Duchess of Rutland, Belvoir Castle has witnessed countless Christmases, each one adding to the tapestry of tradition that we celebrate today. The very walls seem to whisper stories of Christmases past, while our contemporary celebrations write new chapters for future generations.
                </PageText>
                
                <PageText>
                  As you explore the Castle this Christmas, you'll discover that every room has been thoughtfully curated to reflect both the season and the Castle's unique character. From the elegant State Dining Room, where festive feasts might have been served to visiting dignitaries, to the intimate family rooms where Christmas traditions were born, each space offers a glimpse into the Castle's living history.
                </PageText>
                
                <PageText>
                  The Castle's grounds, too, play their part in our Christmas story. The formal gardens, though sleeping under winter's blanket, provide a beautiful backdrop for seasonal walks, while the Castle's commanding position offers breathtaking views across the Vale of Belvoir – a landscape that has inspired artists and poets for centuries.
                </PageText>
                
                <PageText>
                  This Christmas, we invite you to step into our story, to become part of the ongoing narrative of Belvoir Castle. Whether you're discovering the magic of Santa's Grotto, enjoying our Festive Afternoon Tea, or simply wandering through the Castle's magnificent rooms, you're adding your own chapter to our Christmas tale.
                </PageText>
              </PageContent>
            </BookPages>
          )}
        </Book>
      </BookContainer>
    </ModalOverlay>
  );
};

export default ChristmasAboutModal;